import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { type ReactNode, useMemo, useState } from 'react';

import { TRAFFIC_TYPES } from '@/api/common';
import { useVendorActivity } from '@/api/vendor';
import { Toggle } from '@/components/buttons';
import { RangeFilter } from '@/components/filters';
import {
  type InstantSearchDataTableColumnDef,
  useInstantSearchState,
} from '@/components/InstantSearch';
import { getSolQueryParamsNewFromQueryState } from '@/components/InstantSearch/util/search-util';
import { usePageFilterContext } from '@/components/page';
import { Skeleton } from '@/components/Skeleton';
import { Link, SupportLink } from '@/components/typography';
import {
  AUDIENCE_PRIMARY,
  BOT_PRIMARY,
  TAM_PRIMARY,
  UNKNOWN_PRIMARY,
  VISITS_PRIMARY,
} from '@/constants';
import { ChannelFilter, VendorFilter } from '@/features/filters';
import { StackModeToggleButton } from '@/features/StackModeToggleButton';
import {
  ViewThroughPercentCell,
  VisitsTableBarCell,
  VIEW_THROUGH_VISITS_METRIC_FIELDS,
} from '@/features/VisitsTableBarCell';
import useMaxVisitsStats from '@/features/VisitsTableBarCell/useMaxVisitsStats';
import didClickOnStickyPopover from '@/helper/didClickOnStickyPopover';
import { numberFormat } from '@/helper/numberFormatter';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { TableState } from '@/providers/TableState';
import { ROUTES } from '@/router';
import { type WebTrafficSearchParamType } from '@/router/searchParams/webTrafficSearchParams';

import VendorActivityTable, { type VendorActivityTableRow } from './VendorActivityTable';

const DEFAULT_HIDDEN_COLUMNS = [
  'adClicks',
  'countPipelineInfluenced',
  'countClosedwonInfluenced',
  'companiesReached',
  'leadsInfluenced',
  'pipelineFirstTouchAttribution',
  'closedWonFirstTouchAttribution',
  'pipelineLastTouchAttribution',
];

const isVendorRow = (row: VendorActivityTableRow) => row.rowType === 'vendor';
const isSummaryRow = (row: VendorActivityTableRow) => row.rowType === 'campaignSummary';
const isCampaignLoadingRow = (row: VendorActivityTableRow) => row.rowType === 'campaignLoading';

const renderSummaryCell = (row: VendorActivityTableRow, content: ReactNode) => {
  if (isSummaryRow(row)) {
    return <span className="inline-block h-5" />;
  }

  return content;
};

const renderLoadingCell = (row: VendorActivityTableRow, content: ReactNode) => {
  if (isCampaignLoadingRow(row)) {
    return <Skeleton className="h-4 w-full rounded-xs" />;
  }

  return content;
};

const renderLoadingSummaryCell = (row: VendorActivityTableRow, content: ReactNode) =>
  renderLoadingCell(row, renderSummaryCell(row, content));

const getWebTrafficEntityParams = (row: VendorActivityTableRow): WebTrafficSearchParamType => {
  if (isVendorRow(row)) {
    return {
      'w.vendorIds': [row.vendor.id],
    } as WebTrafficSearchParamType;
  }

  if (!row.campaignId) {
    return {} as WebTrafficSearchParamType;
  }

  return {
    'w.campaignIds': [row.campaignId],
  } as WebTrafficSearchParamType;
};

const getOpportunityActivityWithEntityParam = (
  row: VendorActivityTableRow,
  params: {
    vendorParam: string;
    campaignParam: string;
  },
): string => {
  if (isVendorRow(row)) {
    return ROUTES.opportunityActivityWithParams({
      [params.vendorParam]: row.vendor.id,
    } as never);
  }

  if (!row.campaignId) {
    return ROUTES.opportunityActivityWithParams({
      [params.vendorParam]: row.vendor.id,
    } as never);
  }

  return ROUTES.opportunityActivityWithParams({
    [params.campaignParam]: row.campaignId,
  } as never);
};

const VendorActivityTableContainer = () => {
  const {
    pageFilters: { startDate, endDate, audience },
  } = usePageFilterContext();
  const { queryState } = useInstantSearchState();
  const [disabledSeries, setDisabledSeries] = useState<TRAFFIC_TYPES[] | undefined>([]);
  const [visitsMode, setVisitsMode] = useState<'stack' | 'percent'>('percent');
  const [includeViewThroughVisits, setIncludeViewThroughVisits] = useState(false);

  const {
    vendorActivity: data,
    totalResults,
    isLoading,
    error,
  } = useVendorActivity(
    getSolQueryParamsNewFromQueryState(queryState),
    startDate,
    endDate,
    audience?.id,
  );

  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [stickyRow, setStickyRow] = useState<{
    element: HTMLElement;
    rowId: string;
  } | null>(null);

  useOnClickOutside(stickyRow?.element, () => setStickyRow(null), {
    shouldIgnoreEvent: (event) => didClickOnStickyPopover(event.target),
  });

  const maxVisits = useMaxVisitsStats({ data, audience, disabledSeries });
  const maxVisitsWithViewThrough = useMaxVisitsStats({
    data,
    audience,
    disabledSeries,
    metricFields: VIEW_THROUGH_VISITS_METRIC_FIELDS,
    sumWithBaseMetrics: true,
  });
  const visitsBarMax = includeViewThroughVisits ? maxVisitsWithViewThrough : maxVisits;

  const sortFields = ROUTES.vendors.searchParams['v.sortBy'].enumValues;

  const vendorActivityMetrics = useMemo<InstantSearchDataTableColumnDef<VendorActivityTableRow>[]>(
    () => [
      {
        header: t`Channel`,
        id: 'channel.id',
        enableSorting: sortFields.includes('channel.id'),
        minSize: 120,
        flex: 1,
        metricSelector: false,
        filterComponent: (props) => <ChannelFilter {...props} />,
        cellProps: ({ row }) => {
          const field = row.original;

          if (
            field.rowType === 'campaign' ||
            field.rowType === 'campaignSummary' ||
            field.rowType === 'campaignLoading'
          ) {
            if (field.showCampaignLabel) {
              return {
                rowSpan: field.campaignLabelRowSpan ?? 1,
                className: 'bg-gray-100 text-center align-middle text-gray-700',
              };
            }

            return {
              rowSpan: 0,
            };
          }

          return {};
        },
        cell: ({ row }) => {
          const field = row.original;

          if (field.rowType === 'vendor') {
            return (
              <Link variant="caption1" to={ROUTES.channelById(field.vendor.channel?.id)}>
                {field.vendor.channel?.name}
              </Link>
            );
          }

          if (field.showCampaignLabel) {
            return (
              <span>
                <Trans>Campaigns</Trans>
              </span>
            );
          }

          return null;
        },
      },
      {
        header: t`Vendor`,
        id: 'vendorId',
        enableSorting: sortFields.includes('vendorId'),
        minSize: 200,
        flex: 1,
        metricSelector: false,
        filterComponent: (props) => <VendorFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;

          if (isCampaignLoadingRow(field)) {
            return <Skeleton className="h-4 w-full rounded-xs" />;
          }

          if (field.rowType === 'vendor') {
            return (
              <Link
                className="wrap-anywhere"
                variant="caption1"
                weight="bold"
                to={ROUTES.vendorById(field.vendor.id)}
              >
                {field.vendor.name}
              </Link>
            );
          }

          if (field.rowType === 'campaignSummary') {
            return (
              <Link
                className="wrap-anywhere"
                variant="caption1"
                color="aqua"
                to={ROUTES.campaignsWithParams({ 'c.vendor.id': [field.vendor.id] })}
              >
                {field.name}
              </Link>
            );
          }

          if (!field.campaignId) {
            return null;
          }

          return (
            <Link
              className="wrap-anywhere"
              variant="caption1"
              to={ROUTES.campaignById(field.campaignId)}
            >
              {field.name}
            </Link>
          );
        },
      },
      {
        header: t`Spend`,
        id: 'spend',
        size: 110,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('spend'),
        infoPopover: (
          <Trans>
            Spend is reported either through your vendor's API connection or from manually
            calculated spend entered by your team on the{' '}
            <Link variant="body2" color="static-green" to={ROUTES.configureVendors.path}>
              Vendor Spend Page
            </Link>
            .
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          return renderLoadingSummaryCell(
            field,
            numberFormat(field.spend, { isCurrency: true, precision: 0 }),
          );
        },
      },
      {
        header: t`$/Visit`,
        id: 'spendPerTargetVisit',
        size: 95,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('spendPerTargetVisit'),
        infoPopover: (
          <Trans>
            $/Visit shows the cost per audience visit (Spend / Audience Visits). Spend is reported
            either through your vendor's API connection or from manually calculated spend entered by
            your team on the{' '}
            <Link variant="body2" color="static-green" to={ROUTES.configureVendors.path}>
              Vendor Spend Page
            </Link>
            .
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          return renderLoadingSummaryCell(
            field,
            numberFormat(field.spendPerTargetVisit, { isCurrency: true, precision: 2 }),
          );
        },
      },
      {
        header: t`Impressions`,
        id: 'adImpressions',
        size: 120,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('adImpressions'),
        infoPopover: (
          <Trans>
            Volume of impressions shared with Channel99 through the ads integration. This is total
            impressions for the time period and should match your vendors reporting.
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;

          return renderLoadingSummaryCell(
            field,
            Number(field.adImpressions) > 0 ? (
              <span className="inline-flex w-full justify-end">
                <Link
                  variant="caption1"
                  to={ROUTES.webTrafficActivityWithParams({
                    ...getWebTrafficEntityParams(field),
                    'w.metrics.impressions': '[1,]',
                  })}
                >
                  {numberFormat(field.adImpressions)}
                </Link>
              </span>
            ) : (
              0
            ),
          );
        },
      },
      {
        header: t`Clicks`,
        id: 'adClicks',
        size: 90,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('adClicks'),
        infoPopover: (
          <Trans>
            Clicks reported directly from your connected vendor. This should match what your vendor
            is reporting.
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          return renderLoadingSummaryCell(field, numberFormat(field.adClicks));
        },
      },
      {
        header: includeViewThroughVisits ? t`Visits + View Through` : t`Visits`,
        id: 'visits',
        size: 300,
        meta: { align: 'start' },
        enableSorting: sortFields.includes('visits'),
        actionContent: (
          <div className="flex items-center gap-1 py-0.5">
            <Toggle
              variant="ghost"
              size="small"
              shape="square"
              pressed={includeViewThroughVisits}
              aria-label={t`Include view-through visits in chart`}
              onPressedChange={setIncludeViewThroughVisits}
            >
              <Trans>VT</Trans>
            </Toggle>
            <StackModeToggleButton mode={visitsMode} size="small" onChange={setVisitsMode} />
          </div>
        ),
        infoPopover: (
          <Trans>
            Volume of impressions shared with Channel99 through the ads integration. This is total
            impression for the time period and should match your vendors reporting.{' '}
            <SupportLink variant="body2" color="static-green" path="VISITS">
              More details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cellProps: ({ row }) => {
          const field = row.original;

          if (isCampaignLoadingRow(field) || isSummaryRow(field)) {
            return undefined;
          }

          return {
            className: 'cursor-pointer',
            onMouseEnter: () => setHoveredRowId(field.id),
            onMouseLeave: () => setHoveredRowId(null),
            onClick: (event) => {
              if (didClickOnStickyPopover(event.target)) {
                return;
              }

              if (stickyRow?.rowId === field.id) {
                setStickyRow(null);
              } else {
                setStickyRow({ element: event.currentTarget, rowId: field.id });
              }
            },
          };
        },
        cell: ({ row }) => {
          const field = row.original;
          return renderLoadingSummaryCell(
            field,
            <VisitsTableBarCell
              data={[
                {
                  type: audience?.isTAM ? TRAFFIC_TYPES.TAM : TRAFFIC_TYPES.AUDIENCE,
                  label: audience?.isTAM ? t`TAM` : (audience?.name ?? t`Audience`),
                  value: includeViewThroughVisits
                    ? field.targetVisits + (field.targetViewThroughVisits ?? 0)
                    : field.targetVisits,
                  color: audience?.isTAM ? TAM_PRIMARY : AUDIENCE_PRIMARY,
                  disabled: audience?.isTAM
                    ? disabledSeries?.includes(TRAFFIC_TYPES.TAM) === true
                    : disabledSeries?.includes(TRAFFIC_TYPES.AUDIENCE) === true,
                },
                {
                  type: TRAFFIC_TYPES.OTHER,
                  label: t`Other Companies`,
                  value: includeViewThroughVisits
                    ? field.otherVisits + (field.otherViewThroughVisits ?? 0)
                    : field.otherVisits,
                  color: VISITS_PRIMARY,
                  disabled: disabledSeries?.includes(TRAFFIC_TYPES.OTHER) === true,
                },
                {
                  type: TRAFFIC_TYPES.BOT,
                  label: t`Bot`,
                  value: includeViewThroughVisits
                    ? field.botVisits + (field.botViewThroughVisits ?? 0)
                    : field.botVisits,
                  color: BOT_PRIMARY,
                  disabled: disabledSeries?.includes(TRAFFIC_TYPES.BOT) === true,
                },
                {
                  type: TRAFFIC_TYPES.UNRESOLVED,
                  label: t`Unknown`,
                  value: includeViewThroughVisits
                    ? field.unresolvedVisits + (field.unresolvedViewThroughVisits ?? 0)
                    : field.unresolvedVisits,
                  color: UNKNOWN_PRIMARY,
                  disabled: disabledSeries?.includes(TRAFFIC_TYPES.UNRESOLVED) === true,
                },
              ]}
              maxVisits={visitsBarMax}
              displayMode={visitsMode}
              isOpaque={
                (stickyRow == null && hoveredRowId == null) ||
                stickyRow?.rowId === field.id ||
                hoveredRowId === field.id
              }
              isTooltipOpen={hoveredRowId === field.id || stickyRow?.rowId === field.id}
              isTooltipSticky={stickyRow?.rowId === field.id}
              tooltipTitle={field.name}
              tooltipLink={(trafficType: TRAFFIC_TYPES) => {
                const queryParams = {
                  ...getWebTrafficEntityParams(field),
                  'w.metrics.visits': '[1,]',
                } as WebTrafficSearchParamType;

                if (trafficType === TRAFFIC_TYPES.AUDIENCE && audience?.id) {
                  return ROUTES.webTrafficActivityWithParams({
                    ...queryParams,
                    'w.audienceIds': audience.id,
                  });
                } else if (trafficType === TRAFFIC_TYPES.TAM) {
                  return ROUTES.webTrafficActivityWithParams({
                    ...queryParams,
                    'w.inTam': 'true',
                  });
                } else if (trafficType === TRAFFIC_TYPES.OTHER) {
                  if (audience?.isTAM) {
                    return ROUTES.webTrafficActivityWithParams({
                      ...queryParams,
                      'w.inTam': 'false',
                    });
                  }
                  return ROUTES.webTrafficActivityWithParams({
                    ...queryParams,
                    ['w.audienceIds!']: audience?.id,
                  });
                } else if (trafficType === TRAFFIC_TYPES.ALL) {
                  return ROUTES.webTrafficActivityWithParams(queryParams);
                }

                return undefined;
              }}
              onStickyTooltipClose={() => setStickyRow(null)}
            />,
          );
        },
      },
      {
        header: t`VT %`,
        id: 'viewThroughPercent',
        meta: { align: 'end' },
        enableSorting: sortFields.includes('viewThroughPercent'),
        size: 80,
        infoPopover: (
          <Trans>
            VT % shows the share of visits that were view-through only. A view-through visit happens
            when someone views tracked content and later visits through a different direct source
            without clicking that tracked content. This metric is calculated as Total VT Visits /
            (Total VT Visits + Total Visits).
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter type="percent" {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          return renderLoadingSummaryCell(
            field,
            <ViewThroughPercentCell
              tooltipTitle={field.name}
              viewThroughPercent={field.viewThroughPercent}
              rows={[
                {
                  label: audience?.isTAM ? t`TAM` : (audience?.name ?? t`Audience`),
                  color: audience?.isTAM ? TAM_PRIMARY : AUDIENCE_PRIMARY,
                  viewThroughVisits: field.targetViewThroughVisits ?? 0,
                },
                {
                  label: t`Other Companies`,
                  color: VISITS_PRIMARY,
                  viewThroughVisits: field.otherViewThroughVisits ?? 0,
                },
                {
                  label: t`Bot`,
                  color: BOT_PRIMARY,
                  viewThroughVisits: field.botViewThroughVisits ?? 0,
                },
                {
                  label: t`Unknown`,
                  color: UNKNOWN_PRIMARY,
                  viewThroughVisits: field.unresolvedViewThroughVisits ?? 0,
                },
              ]}
            />,
          );
        },
      },
      {
        header: t`Visit Efficiency`,
        id: 'visitEfficiency',
        meta: { align: 'end' },
        enableSorting: sortFields.includes('visitEfficiency'),
        size: 115,
        infoPopover: (
          <Trans>
            Visit efficiency is the ratio of target audience visits to total visits (unknown
            removed) to give a view of how well a vendor/campaign attracts the right audience.{' '}
            <SupportLink variant="body2" color="static-green" path="VISIT_EFFICIENCY">
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter type="percent" {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          return renderLoadingSummaryCell(
            field,
            numberFormat(field.visitEfficiency, { isPercent: true, precision: 1 }),
          );
        },
      },
      {
        header: t`View-Through Visits`,
        id: 'viewThroughVisits',
        meta: { align: 'end' },
        enableSorting: sortFields.includes('viewThroughVisits'),
        size: 140,
        infoPopover: (
          <Trans>
            A view-through visit occurs when a user views an ad or piece of content and does not
            click through but comes to the website directly through a vendor/campaign.{' '}
            <SupportLink variant="body2" color="static-green" path="VIEW_THROUGH_VISITS">
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          return renderLoadingSummaryCell(field, numberFormat(field.viewThroughVisits));
        },
      },
      {
        header: t`Engaged Companies`,
        id: 'engagedCompanies',
        size: 115,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('engagedCompanies'),
        infoPopover: (
          <Trans>Engaged companies are a count of companies which had at least one visit.</Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          return renderLoadingSummaryCell(
            field,
            Number(field.engagedCompanies) > 0 ? (
              <span className="inline-flex w-full justify-end">
                <Link
                  variant="caption1"
                  to={ROUTES.webTrafficActivityWithParams({
                    ...getWebTrafficEntityParams(field),
                    'w.audienceIds': audience?.isTAM ? undefined : audience?.id,
                    'w.inTam': audience?.isTAM ? 'true' : undefined,
                    'w.metrics.visits': '[1,]',
                  })}
                >
                  {numberFormat(field.engagedCompanies)}
                </Link>
              </span>
            ) : (
              0
            ),
          );
        },
      },
      {
        header: t`Companies Reached`,
        id: 'companiesReached',
        size: 115,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('companiesReached'),
        infoPopover: (
          <Trans>
            The total number of unique companies reached within the selected time period. This can
            be from integration impressions (currently only LinkedIn) or from Pixel impressions.
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          return renderLoadingSummaryCell(field, numberFormat(field.companiesReached));
        },
      },
      {
        header: t`Leads Influence`,
        id: 'leadsInfluenced',
        size: 115,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('leadsInfluenced'),
        infoPopover: (
          <Trans>
            Lead influence is given to each vendor or campaign with at least one visit during the
            opportunity influence window chosen before the opportunity create date.
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          return renderLoadingSummaryCell(field, numberFormat(field.leadsInfluenced));
        },
      },
      {
        header: t`Pipeline Influence`,
        id: 'pipelineInfluenced',
        size: 120,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('pipelineInfluenced'),
        infoPopover: (
          <Trans>
            Pipeline influence is given to each vendor or campaign with at least one visit during
            the date range chosen before the opportunity create date.{' '}
            <SupportLink variant="body2" color="static-green" path="PIPELINE_INFLUENCE">
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.pipelineInfluenced;
          return renderLoadingSummaryCell(
            field,
            text === 0 ? (
              numberFormat(text, { isCurrency: true, precision: 0 })
            ) : (
              <Link
                variant="caption1"
                to={getOpportunityActivityWithEntityParam(field, {
                  vendorParam: 'o.vendorIdListPipelineInfluence',
                  campaignParam: 'o.campaignIdListPipelineInfluence',
                })}
              >
                {numberFormat(text, { isCurrency: true, precision: 0 })}
              </Link>
            ),
          );
        },
      },
      {
        header: t`# Pipeline Influenced`,
        id: 'countPipelineInfluenced',
        size: 145,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('countPipelineInfluenced'),
        infoPopover: (
          <Trans>
            Number of opportunities with pipeline influence attributed to this vendor/campaign.
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.countPipelineInfluenced;
          return renderLoadingSummaryCell(
            field,
            text === 0 ? (
              numberFormat(text)
            ) : (
              <Link
                variant="caption1"
                to={getOpportunityActivityWithEntityParam(field, {
                  vendorParam: 'o.vendorIdListPipelineInfluence',
                  campaignParam: 'o.campaignIdListPipelineInfluence',
                })}
              >
                {numberFormat(text)}
              </Link>
            ),
          );
        },
      },
      {
        header: t`Closed Won Influence`,
        id: 'closedWonInfluenced',
        size: 120,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('closedWonInfluenced'),
        infoPopover: (
          <Trans>
            Closed won influence is given to each vendor or campaign with at least one visit during
            the date range chosen and within the timeline of a closed won opportunity.{' '}
            <SupportLink variant="body2" color="static-green" path="CLOSED_WON_INFLUENCE">
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.closedWonInfluenced;
          return renderLoadingSummaryCell(
            field,
            text === 0 ? (
              numberFormat(text, { isCurrency: true, precision: 0 })
            ) : (
              <Link
                variant="caption1"
                to={getOpportunityActivityWithEntityParam(field, {
                  vendorParam: 'o.vendorIdListClosedwonInfluence',
                  campaignParam: 'o.campaignIdListClosedwonInfluence',
                })}
              >
                {numberFormat(text, { isCurrency: true, precision: 0 })}
              </Link>
            ),
          );
        },
      },
      {
        header: t`# Closed Won Influenced`,
        id: 'countClosedwonInfluenced',
        size: 160,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('countClosedwonInfluenced'),
        infoPopover: (
          <Trans>
            Number of closed-won opportunities with influence attributed to this vendor/campaign.
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.countClosedwonInfluenced;
          return renderLoadingSummaryCell(
            field,
            text === 0 ? (
              numberFormat(text)
            ) : (
              <Link
                variant="caption1"
                to={getOpportunityActivityWithEntityParam(field, {
                  vendorParam: 'o.vendorIdListClosedwonInfluence',
                  campaignParam: 'o.campaignIdListClosedwonInfluence',
                })}
              >
                {numberFormat(text)}
              </Link>
            ),
          );
        },
      },
      {
        header: t`Linear Pipeline`,
        id: 'pipelineLinearAttribution',
        size: 120,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('pipelineLinearAttribution'),
        infoPopover: (
          <Trans>
            Linear pipeline accounts for all interactions within the influence window until the
            opportunity creation date and divides the pipeline value equally to each interaction.{' '}
            <SupportLink variant="body2" color="static-green" path="PIPELINE_LINEAR_ATTRIBUTION">
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.pipelineLinearAttribution;
          return renderLoadingSummaryCell(
            field,
            text === 0 ? (
              numberFormat(text, { isCurrency: true, precision: 0 })
            ) : (
              <Link
                variant="caption1"
                to={getOpportunityActivityWithEntityParam(field, {
                  vendorParam: 'o.vendorIdListPipelineLinear',
                  campaignParam: 'o.campaignIdListPipelineLinear',
                })}
              >
                {numberFormat(text, { isCurrency: true, precision: 0 })}
              </Link>
            ),
          );
        },
      },
      {
        header: t`Linear Closed Won`,
        id: 'closedWonLinearAttribution',
        size: 120,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('closedWonLinearAttribution'),
        infoPopover: (
          <Trans>
            Linear closed won accounts for all interactions within the influence window through the
            opportunity close date and divides the pipeline value equally to each interaction.{' '}
            <SupportLink variant="body2" color="static-green" path="CLOSED_WON_LINEAR_ATTRIBUTION">
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.closedWonLinearAttribution;
          return renderLoadingSummaryCell(
            field,
            text === 0 ? (
              numberFormat(text, { isCurrency: true, precision: 0 })
            ) : (
              <Link
                variant="caption1"
                to={getOpportunityActivityWithEntityParam(field, {
                  vendorParam: 'o.vendorIdListClosedwonLinear',
                  campaignParam: 'o.campaignIdListClosedwonLinear',
                })}
              >
                {numberFormat(text, { isCurrency: true, precision: 0 })}
              </Link>
            ),
          );
        },
      },
      {
        header: t`First-Touch Pipeline`,
        id: 'pipelineFirstTouchAttribution',
        size: 120,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('pipelineFirstTouchAttribution'),
        infoPopover: (
          <Trans>
            The first vendor/campaign which was interacted with (visit or non-digital event) from
            the beginning of the influence window to the opportunity create date.{' '}
            <SupportLink
              variant="body2"
              color="static-green"
              path="PIPELINE_FIRST_TOUCH_ATTRIBUTION"
            >
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.pipelineFirstTouchAttribution;
          return renderLoadingSummaryCell(
            field,
            text === 0 ? (
              numberFormat(text, { isCurrency: true, precision: 0 })
            ) : (
              <Link
                variant="caption1"
                to={getOpportunityActivityWithEntityParam(field, {
                  vendorParam: 'o.vendorIdListPipelineFirstTouch',
                  campaignParam: 'o.campaignIdListPipelineFirstTouch',
                })}
              >
                {numberFormat(text, { isCurrency: true, precision: 0 })}
              </Link>
            ),
          );
        },
      },
      {
        header: t`First-Touch Closed Won`,
        id: 'closedWonFirstTouchAttribution',
        size: 120,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('closedWonFirstTouchAttribution'),
        infoPopover: (
          <Trans>
            The first vendor/campaign which was interacted with (visit or non-digital event) from
            the beginning of the influence window to the opportunity close date.{' '}
            <SupportLink
              variant="body2"
              color="static-green"
              path="CLOSED_WON_FIRST_TOUCH_ATTRIBUTION"
            >
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.closedWonFirstTouchAttribution;
          return renderLoadingSummaryCell(
            field,
            text === 0 ? (
              numberFormat(text, { isCurrency: true, precision: 0 })
            ) : (
              <Link
                variant="caption1"
                to={getOpportunityActivityWithEntityParam(field, {
                  vendorParam: 'o.vendorIdListClosedwonFirstTouch',
                  campaignParam: 'o.campaignIdListClosedwonFirstTouch',
                })}
              >
                {numberFormat(text, { isCurrency: true, precision: 0 })}
              </Link>
            ),
          );
        },
      },
      {
        header: t`Last-Touch Pipeline`,
        id: 'pipelineLastTouchAttribution',
        size: 120,
        meta: { align: 'end' },
        enableSorting: sortFields.includes('pipelineLastTouchAttribution'),
        infoPopover: (
          <Trans>
            The vendor/campaign with the previous interaction to the create date of the opportunity.{' '}
            <SupportLink
              variant="body2"
              color="static-green"
              path="PIPELINE_LAST_TOUCH_ATTRIBUTION"
            >
              More Details
            </SupportLink>
          </Trans>
        ),
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => {
          const field = row.original;
          const text = field.pipelineLastTouchAttribution;
          return renderLoadingSummaryCell(
            field,
            text === 0 ? (
              numberFormat(text, { isCurrency: true, precision: 0 })
            ) : (
              <Link
                variant="caption1"
                to={getOpportunityActivityWithEntityParam(field, {
                  vendorParam: 'o.vendorIdListPipelineLastTouch',
                  campaignParam: 'o.campaignIdListPipelineLastTouch',
                })}
              >
                {numberFormat(text, { isCurrency: true, precision: 0 })}
              </Link>
            ),
          );
        },
      },
    ],
    [
      audience,
      sortFields,
      hoveredRowId,
      stickyRow,
      disabledSeries,
      includeViewThroughVisits,
      visitsMode,
      visitsBarMax,
      setVisitsMode,
      setIncludeViewThroughVisits,
    ],
  );

  return (
    <TableState
      tableKey="vendorActivity"
      columns={vendorActivityMetrics}
      defaultHiddenColumns={DEFAULT_HIDDEN_COLUMNS}
    >
      <VendorActivityTable
        data={data}
        columns={vendorActivityMetrics}
        totalResults={totalResults}
        isLoading={isLoading}
        error={error}
        onDisabledSeriesChange={setDisabledSeries}
      />
    </TableState>
  );
};

export default VendorActivityTableContainer;
